import React, {createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState,} from 'react';
import type Keycloak from 'keycloak-js';
import type {KeycloakTokenParsed} from 'keycloak-js';

// Extend the standard token payload with Keycloak realm-specific claims.
interface NutoTokenParsed extends KeycloakTokenParsed {
    preferred_username?: string;
    email?: string;
    realm_access?: {
        roles: string[];
    };
}

export interface AuthContextType {
    isAuthenticated: boolean;
    /** Keycloak preferred_username claim */
    user: string | null;
    /** Keycloak email claim */
    email: string | null;
    /** Current access token — kept reactive so existing components using
     *  `const { token } = useAuth()` continue to work without changes. */
    token: string | null;
    roles: string[];
    /** Ensures a valid (non-expired) token before every API call.
     *  Triggers a silent refresh if the token is close to expiry.
     *  Falls back to keycloak.login() if the refresh fails. */
    getAccessToken: () => Promise<string | null>;
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
    keycloak: Keycloak;
}

const TOKEN_MIN_VALIDITY_SECONDS = 60;

function extractClaims(keycloak: Keycloak): {
    user: string | null;
    email: string | null;
    roles: string[];
} {
    const parsed = keycloak.tokenParsed as NutoTokenParsed | undefined;
    return {
        user: parsed?.preferred_username ?? null,
        email: parsed?.email ?? null,
        roles: parsed?.realm_access?.roles ?? [],
    };
}

export const AuthProvider = ({children, keycloak}: AuthProviderProps) => {
    const [isAuthenticated, setIsAuthenticated] = useState(keycloak.authenticated ?? false);
    const [token, setToken] = useState<string | null>(keycloak.token ?? null);
    const [claims, setClaims] = useState(() => extractClaims(keycloak));
    const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const logout = useCallback(
        () => keycloak.logout({redirectUri: window.location.origin}),
        [keycloak]
    );

    const login = useCallback(() => keycloak.login(), [keycloak]);

    const getAccessToken = useCallback(async (): Promise<string | null> => {
        try {
            await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
            const freshToken = keycloak.token ?? null;
            setToken(freshToken);
            return freshToken;
        } catch {
            // Refresh failed — session expired, force re-login.
            keycloak.login();
            return null;
        }
    }, [keycloak]);

    useEffect(() => {
        keycloak.onAuthSuccess = () => {
            setIsAuthenticated(true);
            setToken(keycloak.token ?? null);
            setClaims(extractClaims(keycloak));
        };

        keycloak.onAuthRefreshSuccess = () => {
            setToken(keycloak.token ?? null);
            setClaims(extractClaims(keycloak));
        };

        keycloak.onAuthError = () => {
            setIsAuthenticated(false);
            setToken(null);
        };

        keycloak.onTokenExpired = () => {
            keycloak
                .updateToken(TOKEN_MIN_VALIDITY_SECONDS)
                .then(() => {
                    setToken(keycloak.token ?? null);
                    setClaims(extractClaims(keycloak));
                })
                .catch(() => logout());
        };

        keycloak.onAuthLogout = () => {
            setIsAuthenticated(false);
            setToken(null);
        };

        // Proactive silent refresh — belt-and-suspenders on top of onTokenExpired.
        refreshIntervalRef.current = setInterval(async () => {
            if (keycloak.authenticated) {
                try {
                    const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
                    if (refreshed) {
                        setToken(keycloak.token ?? null);
                        setClaims(extractClaims(keycloak));
                    }
                } catch {
                    logout();
                }
            }
        }, 30_000);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [keycloak, logout]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user: claims.user,
                email: claims.email,
                token,
                roles: claims.roles,
                getAccessToken,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
